import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import PlanGate from "@/components/PlanGate";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { initAnalytics } from "@/lib/analytics";

// --- EAGER: critici per First Contentful Paint (Landing + Auth)
import Login from "@/pages/Login";
import StaffLogin from "@/pages/StaffLogin";
import Register from "@/pages/Register";
import TracciabilitaLotto from "@/pages/TracciabilitaLotto";
import ScansionaLotto from "@/pages/ScansionaLotto";
import AuthCallback from "@/pages/AuthCallback";
import CookieBanner from "@/components/CookieBanner";
import UpdateBanner from "@/components/UpdateBanner";
import OfflineBanner from "@/components/OfflineBanner";
import TurnoAlertBeeper from "@/components/TurnoAlertBeeper";

// --- LAZY: tutto il resto dell'app autenticata viene caricato solo quando serve
// R54.2: cleanup legacy PMI. Rimossi i 12 duplicati (Oggi/Documenti/Temperature/…)
// e le 20 pagine PMI-only mai renderizzate (Landing, Dashboard, SetupAzienda,
// Fornitori, Ricette, Giacenze, controllo-*, tarature, ecc.). Backend router
// rimane per ora attivo per rotte SA/legacy DB.
const LandingRistorazione = lazy(() => import("@/pages/LandingRistorazione"));
const ServiziHaccp = lazy(() => import("@/pages/ServiziHaccp"));
const PublicBriefManuale = lazy(() => import("@/pages/PublicBriefManuale"));
// R54.6 — Preload rotte principali via webpack magic comment `webpackPrefetch: true`.
// Webpack aggiunge `<link rel="prefetch">` nel `<head>` per queste 6 rotte core →
// il browser scarica i chunk in idle time (dopo l'onload), rendendo la navigazione
// post-login istantanea. Zero cost su First Contentful Paint.
const LayoutRistorazione = lazy(() => import(/* webpackPrefetch: true */ "@/pages/LayoutRistorazione"));
const HomeRistorazione = lazy(() => import(/* webpackPrefetch: true */ "@/pages/HomeRistorazione"));
const ImpostazioniRistorazione = lazy(() => import("@/pages/ImpostazioniRistorazione"));
const SetupPuntiTemperature = lazy(() => import("@/pages/SetupPuntiTemperature"));
const SetupProgrammaPulizie = lazy(() => import("@/pages/SetupProgrammaPulizie"));
const DocumentiRistorazione = lazy(() => import("@/pages/DocumentiRistorazione"));
const TemperatureRistorazione = lazy(() => import(/* webpackPrefetch: true */ "@/pages/TemperatureRistorazione"));
const PulizieRistorazione = lazy(() => import(/* webpackPrefetch: true */ "@/pages/PulizieRistorazione"));
const ScartiRistorazione = lazy(() => import("@/pages/ScartiRistorazione"));
const InfestantiRistorazione = lazy(() => import("@/pages/InfestantiRistorazione"));
const RicevimentiRistorazione = lazy(() => import(/* webpackPrefetch: true */ "@/pages/RicevimentiRistorazione"));
const ProduzioniRistorazione = lazy(() => import("@/pages/ProduzioniRistorazione"));
const EtichetteRistorazione = lazy(() => import("@/pages/EtichetteRistorazione"));
const NonConformitaRistorazione = lazy(() => import(/* webpackPrefetch: true */ "@/pages/NonConformitaRistorazione"));
const FornitoriAnagrafica = lazy(() => import("@/pages/FornitoriAnagrafica"));
const Ispezioni = lazy(() => import("@/pages/Ispezioni"));
const Attestati = lazy(() => import("@/pages/Attestati"));
const MieiAttestati = lazy(() => import("@/pages/MieiAttestati"));
const Aziende = lazy(() => import("@/pages/Aziende"));
const Team = lazy(() => import("@/pages/Team"));
const ImpostazioniSedi = lazy(() => import("@/pages/ImpostazioniSedi"));
const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
const AccountPrivacy = lazy(() => import("@/pages/AccountPrivacy"));
// R60.58: BriefManuale (vecchio SA) rimosso - sostituito da BriefManualeSA.jsx
// R60.58: BriefPubblico (vecchio R13) rimosso - sostituito da PublicBriefManuale.jsx
const PasswordDimenticata = lazy(() => import("@/pages/PasswordDimenticata"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const DashboardAdmin = lazy(() => import("@/pages/DashboardAdmin"));
const Comunicazioni = lazy(() => import("@/pages/Comunicazioni"));
const Magazzino = lazy(() => import("@/pages/Magazzino"));
const Ricette = lazy(() => import("@/pages/Ricette"));
const Costi = lazy(() => import("@/pages/Costi"));
const ProvaGratis = lazy(() => import("@/pages/ProvaGratis"));
const TestIspezione = lazy(() => import("@/pages/TestIspezione"));
const Home = lazy(() => import("@/pages/Home"));
const ManualeHACCP = lazy(() => import("@/pages/ManualeHACCP"));
// R60.58: ManualeWizard rimosso - il nuovo flusso usa /manuale-brief/:token
const ManualeCheckoutSuccess = lazy(() => import("@/pages/ManualeCheckoutSuccess"));
const Tracciabilita = lazy(() => import("@/pages/Tracciabilita"));
const ScegliPiano = lazy(() => import("@/pages/ScegliPiano"));
const RichiediAssistenza = lazy(() => import("@/pages/RichiediAssistenza"));
const AssistenzaAdmin = lazy(() => import("@/pages/AssistenzaAdmin"));
const IMieiTicket = lazy(() => import("@/pages/IMieiTicket"));
const ConsulenteHaccp = lazy(() => import("@/pages/ConsulenteHaccp"));
const CategorieScadenze = lazy(() => import("@/pages/CategorieScadenze"));
const ManualeAutocontrollo = lazy(() => import("@/pages/ManualeAutocontrollo"));
const ManualeHome = lazy(() => import("@/pages/ManualeHome"));
const IgienePersonale = lazy(() => import("@/pages/IgienePersonale"));
const ContrattiSA = lazy(() => import("@/pages/ContrattiSA"));
const Messages = lazy(() => import("@/pages/Messages"));
const StatusPage = lazy(() => import("@/pages/StatusPage"));
const ImpostazioniSA = lazy(() => import("@/pages/ImpostazioniSA"));
const Reports = lazy(() => import("@/pages/Reports"));
const Aiuto = lazy(() => import("@/pages/Aiuto"));
const CheckBrowser = lazy(() => import("@/pages/CheckBrowser"));
const Installa = lazy(() => import("@/pages/Installa"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TerminiServizio = lazy(() => import("@/pages/TerminiServizio"));
const Sicurezza = lazy(() => import("@/pages/Sicurezza"));
const Dpia = lazy(() => import("@/pages/Dpia"));
const SubProcessors = lazy(() => import("@/pages/SubProcessors"));
const Prezzi = lazy(() => import("@/pages/Prezzi"));
const AttivaPiano = lazy(() => import("@/pages/AttivaPiano"));
const PagamentoOk = lazy(() => import("@/pages/PagamentoOk"));
const PagamentoAnnullato = lazy(() => import("@/pages/PagamentoAnnullato"));
const Abbonamento = lazy(() => import("@/pages/Abbonamento"));
const PublicPreventivo = lazy(() => import("@/pages/PublicPreventivo"));
const VerifyReceipt = lazy(() => import("@/pages/VerifyReceipt"));
const PublicEtichetta = lazy(() => import("@/pages/PublicEtichetta"));
const PublicIspezione = lazy(() => import("@/pages/PublicIspezione"));
const CompletaRegistrazioneGoogle = lazy(() => import("@/pages/CompletaRegistrazioneGoogle"));

// Fallback minimo mostrato durante il caricamento dei chunk lazy.
function PageFallback() {
  return (
    <div
      className="min-h-screen flex items-center justify-center text-slate-500"
      data-testid="page-loading"
    >
      Caricamento…
    </div>
  );
}

function Protected({ children }) {
  const { user, checking } = useAuth();
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="auth-loading">
        Caricamento…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && window.location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  // R60.30 — Utenti "manuale-only" hanno accesso SOLO alle pagine del wizard manuale.
  // Qualsiasi altra rotta protetta li rimanda alla dashboard cliente /manuale-home.
  if (user.is_manuale_only) {
    const path = window.location.pathname;
    const allowed =
      path === "/manuale-home" ||
      path.startsWith("/manuale-genera") ||
      path === "/change-password" ||
      path === "/logout";
    if (!allowed) return <Navigate to="/manuale-home" replace />;
  }
  return (
    <>
      {/* R55.5 — Beeper allarme turno: si monta solo per admin/operator con org_id */}
      {(user.role === "admin" || user.role === "operator") && user.org_id && !user.is_manuale_only && (
        <TurnoAlertBeeper />
      )}
      {children}
    </>
  );
}

function RequireRole({ roles, children, fallback = "/app" }) {
  const { user, checking } = useAuth();
  if (checking) return null;
  if (!user) return <Navigate to="/login" replace />;
  // SA in "Vedi come" (impersonating) ha role=admin ma real_role=superadmin.
  const effectiveRole = user.impersonating && user.real_role ? user.real_role : user.role;
  if (!roles.includes(effectiveRole)) return <Navigate to={fallback} replace />;
  return children;
}

// R54.2: variante SEMPRE ristorazione. Admin/operator atterrano su HomeRistorazione.
// SA/Consulente vanno su /aziende (default landing SA dopo rimozione DashboardSA/CRM).
// In "Vedi come" (impersonate) SA vede /oggi (home ristorazione del cliente).
function HomeForRole() {
  const { user } = useAuth();
  if (!user) return null;
  // R60.30 — Utenti manuale-only: mai homepage app, sempre dashboard manuale.
  if (user.is_manuale_only) {
    return <Navigate to="/manuale-home" replace />;
  }
  if (user.role === "admin" || user.role === "operator") {
    return <HomeRistorazione />;
  }
  if (user.role === "superadmin" || user.role === "consultant") {
    if (user.impersonating && user.sa_view_org) {
      return <Navigate to="/oggi" replace />;
    }
    return <Navigate to="/aziende" replace />;
  }
  return <Navigate to="/oggi" replace />;
}

// Root "/": landing pubblica SaaS (R60.12: nuova /home) per visitatori,
// redirect a /app per autenticati. PWA installata (standalone) → login diretto.
function RootRoute() {
  const { user, checking } = useAuth();
  if (checking) return null;
  if (user) return <Navigate to="/app" replace />;
  const isStandalone =
    (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    (typeof navigator !== "undefined" && navigator.standalone === true);
  if (isStandalone) return <Navigate to="/login" replace />;
  return <Navigate to="/home" replace />;
}

export default function App() {
  useEffect(() => {
    initAnalytics();
    // R53.38 — Auto-reload al cambio SW.
    if ("serviceWorker" in navigator) {
      const LAST_RELOAD_KEY = "haccp_sw_last_reload";
      const onSwMessage = (e) => {
        if (e.data?.type !== "SW_ACTIVATED") return;
        const last = parseInt(sessionStorage.getItem(LAST_RELOAD_KEY) || "0", 10);
        if (Date.now() - last < 60_000) return; // anti-loop: max 1 reload/min
        sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
        try { window.location.reload(); } catch { /* noop */ }
      };
      navigator.serviceWorker.addEventListener("message", onSwMessage);
      return () => navigator.serviceWorker.removeEventListener("message", onSwMessage);
    }
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <OfflineBanner />
        <AppErrorBoundary>
          <Suspense fallback={<PageFallback />}>
            <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/landing-ristorazione" element={<LandingRistorazione />} />
            <Route path="/prova-gratis" element={<ProvaGratis />} />
            <Route path="/test-ispezione" element={<TestIspezione />} />
            <Route path="/home" element={<Home />} />
            <Route path="/manuale-haccp" element={<ManualeHACCP />} />
            <Route path="/servizi-haccp" element={<ServiziHaccp />} />
            {/* R54.2 — Rotte marketing PMI legacy: redirect alla landing Ristorazione */}
            <Route path="/multa-haccp" element={<Navigate to="/" replace />} />
            <Route path="/demo-haccp" element={<Navigate to="/" replace />} />
            <Route path="/ristoranti" element={<Navigate to="/" replace />} />
            <Route path="/obblighi-haccp" element={<Navigate to="/" replace />} />
            <Route path="/eventi" element={<Navigate to="/" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/entra" element={<StaffLogin />} />
            <Route path="/auth/google/callback" element={<AuthCallback />} />
            <Route path="/completa-registrazione" element={<CompletaRegistrazioneGoogle />} />
            <Route path="/check-browser" element={<CheckBrowser />} />
            <Route path="/installa" element={<Installa />} />
            <Route path="/register" element={<Register />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/termini" element={<TerminiServizio />} />
            <Route path="/sicurezza" element={<Sicurezza />} />
            <Route path="/dpia" element={<Dpia />} />
            <Route path="/sub-processors" element={<SubProcessors />} />
            <Route path="/prezzi" element={<Prezzi />} />
            <Route path="/attiva-piano" element={<Protected><AttivaPiano /></Protected>} />
            <Route path="/scegli-piano" element={<Protected><ScegliPiano /></Protected>} />
            <Route path="/scegli-piano/successo" element={<Protected><PagamentoOk /></Protected>} />
            <Route path="/richiedi-assistenza" element={<Protected><RichiediAssistenza /></Protected>} />
            <Route path="/richiedi-assistenza/successo" element={<Protected><PagamentoOk /></Protected>} />
            <Route path="/pagamento-ok" element={<Protected><PagamentoOk /></Protected>} />
            <Route path="/pagamento-annullato" element={<Protected><PagamentoAnnullato /></Protected>} />
            <Route path="/abbonamento" element={<Protected><Abbonamento /></Protected>} />
            <Route path="/p/:token" element={<PublicPreventivo />} />
            <Route path="/verifica-ricevuta/:id" element={<VerifyReceipt />} />
            <Route path="/q/etichetta/:id" element={<PublicEtichetta />} />
            <Route path="/q/ispezione/:token" element={<PublicIspezione />} />
            <Route path="/q/m/:token" element={<PublicIspezione manifesto />} />
            {/* R60.58: /brief/:token legacy redirect al nuovo brief completo */}
            <Route path="/brief/:token" element={<Navigate to="/manuale-brief" replace />} />
            <Route path="/manuale-brief/:token" element={<PublicBriefManuale />} />
            <Route path="/password-dimenticata" element={<PasswordDimenticata />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/change-password" element={<Protected><ChangePassword /></Protected>} />
            {/* R54.2 — /setup-azienda deprecato: la config è nel FirstSetupWizard modale + Impostazioni */}
            <Route path="/setup-azienda" element={<Navigate to="/impostazioni" replace />} />
            <Route path="/conformita" element={<Navigate to="/impostazioni" replace />} />
            <Route element={<Protected><LayoutRistorazione /></Protected>}>
              <Route path="/app" element={<HomeForRole />} />
              <Route path="/oggi" element={<HomeRistorazione />} />
              <Route path="/dashboard" element={<DashboardAdmin />} />
              <Route path="/comunicazioni" element={<RequireRole roles={["superadmin"]}><Comunicazioni /></RequireRole>} />
              <Route path="/magazzino" element={<PlanGate><Magazzino /></PlanGate>} />
              <Route path="/ricette" element={<PlanGate><Ricette /></PlanGate>} />
              <Route path="/costi" element={<RequireRole roles={["admin", "superadmin"]}><PlanGate><Costi /></PlanGate></RequireRole>} />
              <Route path="/report" element={<Reports />} />
              <Route path="/documenti" element={<DocumentiRistorazione />} />
              <Route path="/impostazioni" element={<ImpostazioniRistorazione />} />
              <Route path="/setup-temperature" element={<SetupPuntiTemperature />} />
              <Route path="/setup-pulizie" element={<SetupProgrammaPulizie />} />
              <Route path="/attestati" element={<Attestati />} />
              <Route path="/attestati/miei" element={<MieiAttestati />} />
              <Route path="/temperature" element={<TemperatureRistorazione />} />
              <Route path="/pulizie" element={<PulizieRistorazione />} />
              <Route path="/scarti" element={<ScartiRistorazione />} />
              {/* R53.27: tracciabilità via scansione QR (Plus) */}
              <Route path="/tracciabilita/:prodId" element={<TracciabilitaLotto />} />
              <Route path="/scansiona-lotto" element={<ScansionaLotto />} />
              <Route path="/infestanti" element={<InfestantiRistorazione />} />
              {/* R51.10: Manuale AI rimosso dall'app utente. La generazione del Manuale resta sul gestionale SA. */}
              <Route path="/manuale-ai" element={<Navigate to="/manuale" replace />} />
              <Route path="/anagrafica-fornitori" element={<FornitoriAnagrafica />} />
              <Route path="/ricevimenti" element={<RicevimentiRistorazione />} />
              <Route path="/produzioni" element={<ProduzioniRistorazione />} />
              <Route path="/preparazioni" element={<ProduzioniRistorazione />} />
              <Route path="/etichette" element={<EtichetteRistorazione />} />
              <Route path="/ispezioni" element={<Ispezioni />} />
              <Route path="/non-conformita" element={<NonConformitaRistorazione />} />
              <Route path="/aziende" element={<RequireRole roles={["superadmin", "consultant"]}><Aziende /></RequireRole>} />
              <Route path="/team" element={<Team />} />
              <Route path="/impostazioni-sedi" element={<RequireRole roles={["admin", "superadmin"]}><ImpostazioniSedi /></RequireRole>} />
              <Route path="/categorie-scadenze" element={<CategorieScadenze />} />
              <Route path="/manuale" element={<RequireRole roles={["superadmin", "consultant"]}><ManualeAutocontrollo /></RequireRole>} />
              <Route path="/manuale-home" element={<Protected><ManualeHome /></Protected>} />
              {/* R60.58: /manuale-genera legacy redirect — il nuovo flusso parte da /manuale-haccp */}
              <Route path="/manuale-genera" element={<Navigate to="/manuale-home" replace />} />
              <Route path="/manuale-genera/successo" element={<Protected><ManualeCheckoutSuccess /></Protected>} />
              <Route path="/tracciabilita" element={<Protected><Tracciabilita /></Protected>} />
              <Route path="/igiene-personale" element={<IgienePersonale />} />
              <Route path="/contratti" element={<RequireRole roles={["superadmin", "consultant"]}><ContrattiSA /></RequireRole>} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/impostazioni-sa" element={<RequireRole roles={["superadmin"]}><ImpostazioniSA /></RequireRole>} />
              <Route path="/status" element={<RequireRole roles={["superadmin"]}><StatusPage /></RequireRole>} />
              <Route path="/aiuto" element={<Aiuto />} />
              <Route path="/account-privacy" element={<AccountPrivacy />} />
              <Route path="/assistenza-admin" element={<RequireRole roles={["superadmin"]}><AssistenzaAdmin /></RequireRole>} />
              <Route path="/i-miei-ticket" element={<Protected><IMieiTicket /></Protected>} />
              {/* R60.58: /brief-link rimosso — unificato in /sa/brief-manuale */}
              <Route path="/brief-link" element={<Navigate to="/sa/brief-manuale" replace />} />
              {/* R54.2 — Vecchie rotte PMI: redirect a home per non rompere bookmark */}
              <Route path="/allergeni" element={<Navigate to="/oggi" replace />} />
              <Route path="/registro-allergeni" element={<Navigate to="/oggi" replace />} />
              <Route path="/fornitori" element={<Navigate to="/anagrafica-fornitori" replace />} />
              <Route path="/tarature" element={<Navigate to="/oggi" replace />} />
              <Route path="/verifiche-pre" element={<Navigate to="/oggi" replace />} />
              <Route path="/tamponi" element={<Navigate to="/oggi" replace />} />
              <Route path="/scadenze-aperti" element={<Navigate to="/oggi" replace />} />
              <Route path="/controllo-temperature" element={<Navigate to="/temperature" replace />} />
              <Route path="/controllo-pulizie" element={<Navigate to="/pulizie" replace />} />
              <Route path="/controllo-ricevimenti" element={<Navigate to="/ricevimenti" replace />} />
              <Route path="/controllo-allergeni" element={<Navigate to="/oggi" replace />} />
              <Route path="/giacenze" element={<Navigate to="/oggi" replace />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        </AppErrorBoundary>
        <CookieBanner />
        <UpdateBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}
